import { useCallback, useEffect, useMemo, useState } from 'react';
import { mergeRegistryWithImportedGuests } from './guestSheetCsv';
import {
  CSV_FIELD_OPTIONS,
  buildGuestsFromMapping,
  guessCsvMapping,
  parseCsvToTable,
} from './guestCsvMapped';
import { useAdminTheme } from './adminTheme';

export function AdminCsvImportModal({
  open,
  onClose,
  guests,
  onMerged,
  newGuestId,
  btnBase,
  initialCsvText,
  onConsumedBootstrap,
}) {
  const { theme } = useAdminTheme();
  const [step, setStep] = useState('drop');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [notice, setNotice] = useState('');

  const reset = useCallback(() => {
    setStep('drop');
    setHeaders([]);
    setDataRows([]);
    setMapping({});
    setNotice('');
  }, []);

  useEffect(() => {
    if (!open || !initialCsvText) return;
    const { headers: h, dataRows: d, error } = parseCsvToTable(initialCsvText);
    if (error) {
      setNotice(error);
      setStep('drop');
      onConsumedBootstrap?.();
      return;
    }
    if (!h.length) {
      setNotice('No header row found.');
      setStep('drop');
      onConsumedBootstrap?.();
      return;
    }
    setHeaders(h);
    setDataRows(d);
    setMapping(guessCsvMapping(h));
    setNotice('');
    setStep('map');
    onConsumedBootstrap?.();
  }, [open, initialCsvText, onConsumedBootstrap]);

  const close = useCallback(() => {
    reset();
    onClose?.();
  }, [onClose, reset]);

  const onFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const { headers: h, dataRows: d, error } = parseCsvToTable(text);
      if (error) {
        setNotice(error);
        return;
      }
      if (!h.length) {
        setNotice('No header row found.');
        return;
      }
      setHeaders(h);
      setDataRows(d);
      setMapping(guessCsvMapping(h));
      setNotice('');
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const previewRows = useMemo(() => dataRows.slice(0, 5), [dataRows]);

  const applyImport = useCallback(() => {
    const { guests: imported, warnings } = buildGuestsFromMapping(dataRows, mapping, newGuestId);
    if (!imported.length) {
      setNotice(warnings.join(' ') || 'No rows could be imported.');
      return;
    }
    const merged = mergeRegistryWithImportedGuests(guests, imported, newGuestId);
    onMerged(merged, {
      msg: `Imported ${imported.length} row(s). Registry has ${merged.length} guests.`,
      warnings,
    });
    close();
  }, [dataRows, mapping, guests, newGuestId, onMerged, close]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Import CSV"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10005,
        background: theme.modalOverlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))`,
        boxSizing: 'border-box',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="admin-csv-import-panel"
        style={{
          width: 'min(920px, 100%)',
          boxSizing: 'border-box',
          maxHeight: 'min(90vh, 900px)',
          overflow: 'auto',
          background: theme.bgPanel,
          borderRadius: 12,
          border: `1px solid ${theme.border}`,
          boxShadow:
            theme.mode === 'dark'
              ? '0 24px 60px rgba(0,0,0,0.55)'
              : '0 18px 50px rgba(0,0,0,0.12)',
          padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 650, color: theme.text }}>Import CSV</h2>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.muted, lineHeight: 1.45 }}>
              Map each column to a guest field. Email does not need to be valid or present; rows still need some data
              (e.g. name, phone, group, address, or notes). Existing guests update when the imported email matches
              theirs.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            style={{
              ...btnBase,
              padding: '6px 12px',
              border: `1px solid ${theme.border}`,
              background: theme.bgInput,
              color: theme.muted,
            }}
          >
            Cancel
          </button>
        </div>

        {step === 'drop' ? (
          <label
            style={{
              display: 'block',
              marginTop: 20,
              padding: 36,
              borderRadius: 10,
              border: `2px dashed ${theme.border}`,
              background: theme.bgMuted,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                onFile(f);
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Drop a CSV file here or tap to choose</span>
            <span style={{ display: 'block', marginTop: 8, fontSize: 12, color: theme.muted }}>
              UTF-8, comma-separated, header row in row 1
            </span>
          </label>
        ) : null}

        {step === 'map' ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ overflowX: 'auto', marginBottom: 14 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
                <thead>
                  <tr>
                    {headers.map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.muted,
                          fontWeight: 600,
                          verticalAlign: 'bottom',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }} title={h}>
                          {h || `Column ${i + 1}`}
                        </div>
                        <select
                          value={
                            Object.entries(mapping).find(([, col]) => col === i)?.[0] || '_skip'
                          }
                          onChange={(e) => {
                            const field = e.target.value;
                            setMapping((prev) => {
                              const next = { ...prev };
                              for (const k of Object.keys(next)) {
                                if (next[k] === i) delete next[k];
                              }
                              if (field !== '_skip') {
                                for (const k of Object.keys(next)) {
                                  if (k === field) delete next[k];
                                }
                                next[field] = i;
                              }
                              return next;
                            });
                          }}
                          style={{
                            marginTop: 6,
                            width: '100%',
                            maxWidth: 160,
                            fontSize: 11,
                            padding: '4px 6px',
                          }}
                        >
                          {CSV_FIELD_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, ri) => (
                    <tr key={ri}>
                      {headers.map((_, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: '6px 10px',
                            borderBottom: `1px solid ${theme.border}`,
                            color: theme.text,
                            maxWidth: 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={String(row[ci] ?? '')}
                        >
                          {String(row[ci] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: theme.muted }}>
              Showing first {previewRows.length} data rows · {dataRows.length} total rows
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={applyImport}
                style={{
                  ...btnBase,
                  padding: '10px 18px',
                  border: 'none',
                  background: theme.accent,
                  color: theme.onAccent,
                  minHeight: 44,
                }}
              >
                Import {dataRows.length} row(s)
              </button>
              <button
                type="button"
                onClick={() => setStep('drop')}
                style={{
                  ...btnBase,
                  padding: '10px 18px',
                  border: `1px solid ${theme.border}`,
                  background: theme.bgInput,
                  color: theme.text,
                }}
              >
                Choose different file
              </button>
            </div>
          </div>
        ) : null}

        {notice ? (
          <p style={{ margin: '14px 0 0', fontSize: 13, color: theme.danger }} role="alert">
            {notice}
          </p>
        ) : null}
      </div>
    </div>
  );
}
